import { Cargo } from '../interfaces/cargo';
import { TransportCompany } from '../interfaces/transportCompany';

/**
 * Listados de facturación y generación de archivos contables: la empresa de
 * transporte siempre está aplicada (pastilla) para no mezclar empresas.
 * Pagos, aprobaciones, cumplidos RNDC, consulta de viáticos y validación de
 * servicios no aplican.
 */
const TRANSPORT_COMPANY_CONTEXT_TYPE_LISTS = [
  'charges',
  'withoutPayment',
];

export function usesTransportCompanyContext(typeList: string): boolean {
  return !!typeList && TRANSPORT_COMPANY_CONTEXT_TYPE_LISTS.includes(typeList);
}

/**
 * Query params para volver desde un servicio a su listado: en facturación y
 * generación de archivos el listado arranca con la empresa de transporte del servicio en
 * vez de la primera (list-filter lee ?transportCompanyId=).
 */
export function transportCompanyListQueryParams(typeList: string, cargo: Cargo): { [key: string]: string } {
  const transportCompanyId = cargo && cargo.transportCompany && cargo.transportCompany.id;
  return usesTransportCompanyContext(typeList) && transportCompanyId ? { transportCompanyId } : {};
}

export interface CargosTransportNit {
  consistent: boolean;
  transportNit: string | null;
}

/**
 * Resuelve la empresa de transporte común a un grupo de servicios.
 * - Todos con la misma empresa: consistente, con ese NIT.
 * - Ninguno con empresa (servicios previos a la división): consistente, sin NIT,
 *   para que las consultas se sigan haciendo como antes.
 * - Empresas distintas, o mezcla con y sin empresa: inconsistente.
 */
export function resolveCargosTransportNit(cargos: Cargo[]): CargosTransportNit {
  const nits = (cargos || []).map(cargo =>
    cargo && cargo.transportCompany && cargo.transportCompany.id ? cargo.transportCompany.id : null
  );
  const distinct = nits.filter((nit, index) => nits.indexOf(nit) === index);
  if (distinct.length > 1) { return { consistent: false, transportNit: null }; }
  return { consistent: true, transportNit: distinct.length ? distinct[0] : null };
}

export interface TransportCompanyBillingGroup {
  transportCompanyId: string;
  name: string;
  cargos: Cargo[];
  /** Prefacturas del grupo (solo en "Confirmar cobro por prefactura"). */
  preBills?: number[];
  /** Detalle por prefactura: el diálogo lista prefacturas en vez de servicios. */
  preBillRows?: { consecutive: number; cargos: Cargo[] }[];
}

/**
 * Agrupa servicios por empresa de transporte para facturar un solo grupo a la
 * vez. Los servicios sin empresa quedan aparte: no se pueden facturar junto a
 * los que sí la tienen.
 */
export function groupCargosByTransportCompany(cargos: Cargo[]): {
  groups: TransportCompanyBillingGroup[];
  withoutTransportCompany: Cargo[];
} {
  const groups: TransportCompanyBillingGroup[] = [];
  const withoutTransportCompany: Cargo[] = [];
  (cargos || []).forEach(cargo => {
    const transportCompany = cargo && cargo.transportCompany;
    if (!transportCompany || !transportCompany.id) {
      withoutTransportCompany.push(cargo);
      return;
    }
    let group = groups.find(item => item.transportCompanyId === transportCompany.id);
    if (!group) {
      group = { transportCompanyId: transportCompany.id, name: transportCompany.name || transportCompany.id, cargos: [] };
      groups.push(group);
    }
    group.cargos.push(cargo);
  });
  return { groups, withoutTransportCompany };
}

/**
 * Agrupa prefacturas por la empresa de transporte de la prefactura (no la de sus
 * servicios). Las que no tienen empresa quedan aparte para mostrarse como error.
 */
export function groupPreBillsByTransportCompany(
  preBills: { consecutive: number; transportCompany?: TransportCompany; cargos: Cargo[] }[],
  transportCompanyNames: { [transportCompanyId: string]: string }
): { groups: TransportCompanyBillingGroup[]; withoutTransportCompany: number[] } {
  const groups: TransportCompanyBillingGroup[] = [];
  const withoutTransportCompany: number[] = [];
  (preBills || []).forEach(preBill => {
    const transportCompany = preBill.transportCompany;
    if (!transportCompany || !transportCompany.id) {
      withoutTransportCompany.push(preBill.consecutive);
      return;
    }
    let group = groups.find(item => item.transportCompanyId === transportCompany.id);
    if (!group) {
      group = {
        transportCompanyId: transportCompany.id,
        name: (transportCompanyNames && transportCompanyNames[transportCompany.id]) || transportCompany.name || transportCompany.id,
        cargos: [],
        preBills: [],
        preBillRows: []
      };
      groups.push(group);
    }
    group.cargos.push(...(preBill.cargos || []));
    group.preBills.push(preBill.consecutive);
    group.preBillRows.push({ consecutive: preBill.consecutive, cargos: preBill.cargos || [] });
  });
  return { groups, withoutTransportCompany };
}

